import { add, differenceInDays, format, isValid, parseISO, startOfDay } from "date-fns";
import { nb } from "date-fns/locale";
import type { DateInterval, DeliveryWindowPeriod } from "../../common/types/deliveryTypes";
import type { M3OrderStatusCode } from "../../common/zodSchemas/delivery";
import type { DSPOrderWithParsedDeliveryDate } from "./deliveryApi";
import type { ClientDelivery, ClientDeliveryOrderStatus, ClientDeliveryStatus } from "./deliveryStore";

const timeRegex = /^(\d{2}):(\d{2})$/;

export const getIntervalForDateAndTimes = (date: Date, period: DeliveryWindowPeriod): DateInterval | null => {
   const day = startOfDay(date);
   const startMatch = timeRegex.exec(period.start);
   const stopMatch = timeRegex.exec(period.stop);
   if (!startMatch || !stopMatch) {
      return null;
   }
   const startHour = parseInt(startMatch[1], 10);
   const startMinute = parseInt(startMatch[2], 10);
   const stopHour = parseInt(stopMatch[1], 10);
   const stopMinute = parseInt(stopMatch[2], 10);

   const from = add(day, { hours: startHour, minutes: startMinute });
   const to = add(day, { hours: stopHour, minutes: stopMinute });

   return { from, to };
};

export const getIntervalFromEtaWindow = (from?: string | null, to?: string | null): DateInterval | null => {
   if (!from || !to) {
      return null;
   }
   const fromDate = parseISO(from);
   const toDate = parseISO(to);
   if (!isValid(fromDate) || !isValid(toDate) || fromDate >= toDate) {
      return null;
   }
   return { from: fromDate, to: toDate };
};

export const formatIntervalTimes = (interval: DateInterval) =>
   `mellom kl. ${format(interval.from, "k:mm")} og ${format(interval.to, "k:mm")}`;

const formatDay = (deliveryDate: Date, relativeToDate: Date) => {
   const difference = differenceInDays(startOfDay(deliveryDate), startOfDay(relativeToDate));
   if (difference === 0) {
      return "i dag";
   }
   if (difference === 1) {
      return "i morgen";
   }
   if (difference === -1) {
      return "i går";
   }
   return format(deliveryDate, "d. MMMM", { locale: nb });
};

const deliverableStatuses: ClientDeliveryStatus[] = ["INVOICED", "IN_TRANSIT", "PACKED", "PACKING", "PLANNED", "RECEIVED"];

export const getFormattedDeliveryInfoText = (delivery: ClientDelivery, nowDatetime: Date): string => {
   const { deliveryInterval, status } = delivery;

   if (deliveryInterval) {
      if (deliverableStatuses.includes(status)) {
         return `Forventet levering ${formatDay(deliveryInterval.from, nowDatetime)} ${formatIntervalTimes(deliveryInterval)}`;
      }
   }
   const { deliveredTime } = delivery;
   if (status === "DELIVERED" && deliveredTime) {
      return `Levert ${formatDay(startOfDay(deliveredTime), startOfDay(nowDatetime))} kl. ${format(deliveredTime, "k:mm")}`;
   }

   return "";
};

export const getFormattedRoutePositionText = (delivery: ClientDelivery): string => {
   const { status, stopNumber, currentStopNumber } = delivery;
   if (!stopNumber) {
      return "";
   }
   if (status === "PLANNED") {
      return `Du er levering nr ${stopNumber} på ruten.`;
   }
   // Without currentStopNumber in transit, the driver has skipped this stop, so the route position is left out.
   if (status === "IN_TRANSIT" && currentStopNumber) {
      return `Bilen er på levering ${currentStopNumber} for dagen. Du er levering nr ${stopNumber} på ruten.`;
   }
   return "";
};

const m3OrderStatusToClientStatus = (status: M3OrderStatusCode): ClientDeliveryOrderStatus => {
   switch (status) {
      case "0":
      case "20":
      case "22":
      case "33":
         return "RECEIVED";
      case "44":
         return "PACKING";
      case "66":
      case "69":
         return "PACKED";
      case "77":
      case "79":
         return "INVOICED";
      case "90":
         return "DELETED";
      case "99":
         return "CANNOT_DELIVER";
      default:
         return "RECEIVED";
   }
};

// NOT numeric order!
// implicit order is "99", "90", "0", "20", "22", "33", "44", "66", "77", "79";
export const getHighestClientDeliveryOrderStatus = (
   orders: DSPOrderWithParsedDeliveryDate[]
): ClientDeliveryOrderStatus | null => {
   const highestM3Status = orders.reduce((highest: M3OrderStatusCode, order) => {
      const orderM3Status = order.orderStatusHighest as M3OrderStatusCode | null;
      if (orderM3Status && orderM3Status !== highest) {
         if (orderM3Status === "99") {
            return highest;
         }
         if (orderM3Status === "90") {
            return highest === "99" ? "90" : highest;
         }
         const orderStatusNum = parseInt(orderM3Status, 10);
         const highestStatusNum = parseInt(highest, 10);
         if (highestStatusNum >= 90) {
            return orderM3Status;
         }
         return orderStatusNum > highestStatusNum ? orderM3Status : highest;
      }
      return highest;
   }, "99" as M3OrderStatusCode);

   return m3OrderStatusToClientStatus(highestM3Status);
};

export const getDeliveryBannerTitleFromStatus = (status: ClientDeliveryStatus) => {
   if (status === "RECEIVED") {
      return "Vi har mottatt ordren din!";
   }
   if (status === "PACKING") {
      return "Vi pakker din ordre";
   }
   if (status === "PACKED") {
      return "Ordren din er pakket";
   }
   if (status === "INVOICED") {
      return "Ordren din er pakket og faktura er tilgjengelig";
   }
   if (status === "PLANNED") {
      return "Vi jobber med å laste varene dine på bilen. Ordren din er straks på vei!";
   }
   if (status === "IN_TRANSIT") {
      return "Leveringen er på vei til deg!";
   }
   if (status === "DELIVERED") {
      return "Ordren din er levert";
   }
   return "";
};
